import React, { useEffect, useState, useMemo, useCallback, useRef, startTransition } from 'react';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { IconExternalLink, IconAdjustmentsHorizontal, IconChevronRight, IconChevronLeft, IconArrowNarrowRight } from '@tabler/icons-react';
import { getReportsData } from 'api/getReportsData';
import { getAdHocReportsForManageSaved } from 'api/getAdHocReportsForManageSaved';
import { useSelector } from 'react-redux';
import { trackClick, trackingActions, trackingTypes } from '../../../utils/tracking';
import { store } from '../../../store/store';
import * as ReportTypes from '../../frontoffice/actions/types';
import { formatReptSource } from '../../../utils/formatReptSource';
import SegmentedTabs from './SegmentedTabs';
import { sortFavoritedByAccessDateDesc, sortByAccessDateDesc } from './reportSummaryHelper';
import * as FavoriteReportActions from '../../frontoffice/favoriteReport/actions/action';
import ManageSavedReportsDrawer from './ManageSavedReportsDrawer';
import { AppConfig } from 'AppConfig';
import { OPEN_ALERT_DIALOG } from 'store/actions/appstate';
import { hasPermission, hasSmartCardViewPermission } from 'utils/common';
import ReportSummaryItem from './ReportSummaryItem';
import ReportSectionLoading from 'features/frontoffice/component/reportSectionLoading';
import * as Constant from 'common/Constant';
import { AlertDialog } from 'features/ad-hoc/alertDialog';
import * as ReportActions from '../../frontoffice/actions/actions';

// Simple in-memory cache to avoid refetching reports
const reportsCache = { data: null, ts: 0, tenantCd: null };
const CACHE_TTL_MS = 60_000; // 1 minute

const CARD_WIDTH_COMPACT = 288;
const CARD_WIDTH_DEFAULT = 352;
const CARD_GAP = 24;
const CARD_HEIGHT = 245;
const COMPACT_VIEWPORT_MAX = 400;

const reconcileFavorites = (list, favoritesSource) => {
  if (!Array.isArray(list) || !Array.isArray(favoritesSource)) return list;
  const favoriteMap = new Map();
  favoritesSource.forEach((fav) => {
    if (fav && fav.id != null) {
      favoriteMap.set(String(fav.id), fav);
    }
  });
  let changed = false;
  const next = list.map((item) => {
    if (!item || item.id == null) return item;
    const match = favoriteMap.get(String(item.id));
    if (match) {
      changed = true;
      return { ...item, ...match, favorited: true, createdDttm: match?.createdDttm ?? item?.createdDttm ?? null };
    }
    if (!item.favorited) return item;
    changed = true;
    return { ...item, favorited: false };
  });
  return changed ? next : list;
};

const resetToStandardReportsView = () => {
  sessionStorage.setItem('reportsType', AppConfig.REPORT_CATEGORY.LEGACY.code);
  sessionStorage.removeItem('sortBy');
  sessionStorage.setItem('scrollToReports', 'true');
};

export const SeeAllReportsLink = ({ sx }) => (
  <Link
    component={RouterLink}
    to="/bi/reports"
    onClick={resetToStandardReportsView}
    underline="none"
    sx={{
      fontSize: '14px',
      fontStyle: 'normal',
      fontFamily: 'Noto Sans',
      fontWeight: 600,
      color: '#2F54A5',
      display: 'inline-flex',
      alignItems: 'center',
      lineHeight: '150%',
      letterSpacing: '0%',
      gap: 0.5,
      '&:hover': { color: '#068DF1', textDecoration: 'none', background: 'transparent' },
      ...sx,
    }}>
    See All Reports
    <IconArrowNarrowRight size={16} />
  </Link>
);

// Outlined action button for the Reports-page header (Create Custom Report, Manage Saved Reports).
const ReportActionButton = ({ startIcon, onClick, children }) => (
  <Button
    variant="outlined"
    startIcon={startIcon}
    onClick={onClick}
    sx={{
      width: { xs: '100%', sm: 'auto' },
      height: '36px',
      fontSize: '14px',
      fontFamily: 'Noto Sans, sans-serif',
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: '24px',
      padding: '8px 12px',
      borderRadius: '24px',
      textTransform: 'none',
      borderColor: '#2F54A5',
      color: '#2F54A5',
      '&:hover': {
        backgroundColor: '#D8E6FF',
        borderColor: '#001A47',
        color: '#001A47'
      },
      '& .MuiButton-startIcon': { marginLeft: '0px', marginRight: '6px' },
    }}>
    {children}
  </Button>
);

const ReportSummary = React.memo(({ isReports = false }) => {
  const [reportsData, setReportsData] = useState(() => []);
  const [adHocReportsData, setAdHocReportsData] = useState(() => []);
  const [isReportLoading, setIsReportLoading] = useState(true);
  const location = useLocation();
  const [tabValue, setTabValue] = useState(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    return tab === 'saved' ? 'saved' : 'recently_viewed';
  });
  const [isManageSavedOpen, setIsManageSavedOpen] = useState(false);
  const permissions = useSelector((state) => state.user.permissions);
  const user = useSelector((state) => state.user);
  const reduxFavorites = useSelector((state) => state.favoriteReport?.favoriteReports);
  const reduxTopFavorites = useSelector((state) => state.favoriteReport?.topFavorites);
  const topFavoritesLoading = useSelector((state) => state.favoriteReport?.topFavoritesLoading);
  const currentTenantCd = useSelector((state) => state.user.currentTenantCd);
  const canViewSmartCards = hasSmartCardViewPermission(user);
  const canViewAdHoc = hasPermission(AppConfig.PERMISSION.FO_AD_HOC, permissions);
  const scrollContainerRef = useRef(null);
  const headerRef = useRef(null);
  const greyRef = useRef(null);
  const controlsRef = useRef(null);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  const [hasOverflow, setHasOverflow] = useState(false);


  const [viewportWidth, setViewportWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1440);

  useEffect(() => {
    let raf = 0;
    const onResize = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        setViewportWidth(window.innerWidth);
      });
    };
    window.addEventListener('resize', onResize, { passive: true });
    return () => {
      window.removeEventListener('resize', onResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const carouselConfig = useMemo(() => {
    const cardWidth = viewportWidth < COMPACT_VIEWPORT_MAX ? CARD_WIDTH_COMPACT : CARD_WIDTH_DEFAULT;
    return { cardWidth, gap: CARD_GAP };
  }, [viewportWidth]);

  const onClickTableauAdhoc = () => {
    store.dispatch({
      type: OPEN_ALERT_DIALOG,
      payload: true
    });
  };

  const layout = useMemo(() => ({ cardHeight: CARD_HEIGHT, gapValue: CARD_GAP }), []);

  useEffect(() => {
    store.dispatch(FavoriteReportActions.getFavoriteReport());
  }, []);

  const favMap = useMemo(() => new Map((Array.isArray(reduxFavorites) ? reduxFavorites : [])
    .filter((f) => f?.id != null)
    .map((f) => [String(f.id), f])), [reduxFavorites]);
  
  const favoriteReports = useMemo(() => {
    const list = Array.isArray(reportsData) ? reportsData : [];
    const enriched = list.map((r) => {
      const rid = r?.id != null ? String(r.id) : '';
      const match = favMap.get(rid);
      if(match  || r?.favorited) {
        return { ...r, favorited: true, createdDttm: match?.createdDttm ?? r?.createdDttm ?? null };
      }
      return { ...r, favorited: false };
    });
    const adHocEnriched = (Array.isArray(adHocReportsData) ? adHocReportsData : []).map((r) => {
      const rid = r?.id != null ? String(r.id) : '';
      const match = favMap.get(rid);
      return {
        ...r,
        favorited: favMap.has(rid),
        createdDttm: match?.createdDttm ?? r?.createdDttm ?? null
      };
    });

    const combined = sortFavoritedByAccessDateDesc([...enriched, ...adHocEnriched]);
    if (!canViewSmartCards) {
      return combined.filter((r) => r?.category !== AppConfig.REPORT_CATEGORY.SMART.code);
    }
    return combined;
  }, [favMap, reportsData, adHocReportsData, canViewSmartCards]);
  
  useEffect(() => {
   const el = scrollContainerRef.current;
   if (!el || !hasOverflow) return;
   let snapTimeout = null;
   const originalSnapType = el.style.scrollSnapType || window.getComputedStyle(el).scrollSnapType;


   const handleWheel = (e) => {
    // Only respond to intentional horizontal scroll (deltaX dominant).
    // Vertical scrolling (deltaY dominant) should pass through to page scroll.
    if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
    const delta = e.deltaX;
    const canScrollLeft = el.scrollLeft > 0;
    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    const canScrollRight = Math.ceil(el.scrollLeft) < maxScrollLeft;
    const isScrollingLeft = delta < 0;
    const isScrollingRight = delta > 0;
    if((isScrollingLeft && canScrollLeft) || (isScrollingRight && canScrollRight)) {
    e.preventDefault();
     el.style.scrollSnapType = 'none';
     el.scrollLeft += delta;
     clearTimeout(snapTimeout);
      snapTimeout = setTimeout(() => {
        el.style.scrollSnapType = originalSnapType;
      }, 100);
    };
  }
   el.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', handleWheel);
      if(snapTimeout) clearTimeout(snapTimeout);
    };
  }, [hasOverflow]);

  // Fetch with cache and normalize (re-runs when tenant changes)
  useEffect(() => {
    let active = true;
    const now = Date.now();

    const fetchReportsData = async () => {
      try {
        const response = await getReportsData();
        const sorted = sortByAccessDateDesc(response);
        if (!active) return;
        reportsCache.data = sorted;
        reportsCache.ts = Date.now();
        reportsCache.tenantCd = currentTenantCd;
        setReportsData(sorted);
      } catch (error) {
        if (active) console.error('Error fetching reports data:', error);
      }
    };
    const fetchAdHocData = async () => {
      try {
        const adHocResponse = await getAdHocReportsForManageSaved();
        if (!active) return;
        startTransition(() => {
          setAdHocReportsData(adHocResponse);
        });
      } catch (error) {
        if (active) console.error('Error fetching ad-hoc reports:', error);
      }
    };

    if (canViewAdHoc) {
      fetchAdHocData();
    } else {
      setAdHocReportsData([]);
    }

    const isReportsCacheWarm =
      reportsCache.data &&
      reportsCache.tenantCd === currentTenantCd &&
      now - reportsCache.ts < CACHE_TTL_MS;

    if (isReportsCacheWarm) {
      if (active) {
        setReportsData(reportsCache.data);
      }
      setIsReportLoading(false);
    } else {
      setIsReportLoading(true);
      fetchReportsData().finally(() => {
        if (active) setIsReportLoading(false);
      });
    }

    store.dispatch(FavoriteReportActions.getTopFavoriteReports());
    return () => {
      active = false;
    };
  }, [currentTenantCd, canViewAdHoc]);

  const resetCarouselScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.scrollTo({ left: 0, behavior: 'auto' });
    setIsAtStart(true);
    setIsAtEnd(false);
  }, []);

  // Tab change
  const handleTabsChange = useCallback((next) => {
    if (!next) return;
    setTabValue(next);
  }, []);

  // Cards by tab
  const cardsForTab = useMemo(() => {
    let items;
    switch (tabValue) {
      case 'recently_viewed':
        items = Array.isArray(reportsData) ? reportsData.slice(0, 9) : [];
        break;
      case 'needs_attention':
        items = [];
        break;
      case 'saved':
        items = Array.isArray(reduxTopFavorites) ? reduxTopFavorites : [];
        break;
      default:
        items = [];
    }
    if (!canViewSmartCards) {
      return items.filter((r) => r?.category !== AppConfig.REPORT_CATEGORY.SMART.code);
    }
    return items;
  }, [tabValue, reportsData, reduxTopFavorites, canViewSmartCards]);

  const showArrows = hasOverflow;

  const buttonRowBleed = viewportWidth >= COMPACT_VIEWPORT_MAX && viewportWidth < Constant.BREAKPOINT.tablet;

  const updateScrollState = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const tolerance = 2;
    setIsAtStart(el.scrollLeft <= tolerance);
    setIsAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - tolerance);
    setHasOverflow(el.scrollWidth > el.clientWidth + tolerance);
  }, []);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        updateScrollState();
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    updateScrollState();
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [updateScrollState, cardsForTab]);

  useEffect(() => {
    resetCarouselScroll();
  }, [currentTenantCd, tabValue, cardsForTab.length, resetCarouselScroll]);

  useEffect(() => {
    updateScrollState();
  }, [cardsForTab, viewportWidth, updateScrollState]);

  const getPageScrollAmount = useCallback((el) => {
    const measured = el.querySelector('[data-carousel-card]')?.offsetWidth || carouselConfig.cardWidth;
    const step = measured + carouselConfig.gap;
    const perPage = Math.max(1, Math.floor((el.clientWidth + carouselConfig.gap) / step));
    return step * perPage;
  }, [carouselConfig.cardWidth, carouselConfig.gap]);

  const handlePrevious = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const target = Math.max(el.scrollLeft - getPageScrollAmount(el), 0);
    el.scrollTo({ left: target, behavior: 'smooth' });
  }, [getPageScrollAmount]);

  const handleNext = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const target = Math.min(el.scrollLeft + getPageScrollAmount(el), el.scrollWidth - el.clientWidth);
    el.scrollTo({ left: target, behavior: 'smooth' });
  }, [getPageScrollAmount]);

  const tableauServiceUrl = useSelector((state) => state.user.tableauServiceUrl);
  const navigate = useNavigate();

  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (tab === 'saved') {
      setTabValue('saved');
    }
  }, [location.search]);

  const handleReportClick = useCallback(
    (card) => {
      if (card?.id) {
        trackClick(trackingTypes.REPORT, trackingActions.READ, card.id, (card.category ?? '') + ';' + (card.displayText ?? ''));
      }

      // Determine reportBaseUrl based on current route
      let reportBaseUrl;
      let reportOriginComponent = '';
      if (location.pathname.includes('/reports')) {
        reportBaseUrl = '/bi/reports';
      } else if (location.pathname.includes('/smart-cards')) {
        reportBaseUrl = '/bi/smart-cards';
      } else if (location.pathname.includes('/add-reports')) {
        reportBaseUrl = '/bi/add-reports';
      } else if (location.pathname.includes('/manage-reports')) {
        reportBaseUrl = '/bi/manage-reports';
      } else {
        reportBaseUrl = '/bi';
        reportOriginComponent = Constant.REPORT_SUMMARY_HOME;
      }

      const params = new URLSearchParams(location.search);
      if (tabValue !== 'recently_viewed') {
        params.set('tab', tabValue);
      } else {
        params.delete('tab');
      }
      const search = params.toString();
      if (search) {
        reportBaseUrl += `?${search}`;
      }

      const [workbook, dashboard] = (card.reptSource || '').split('/');
      navigate(`${'/bi'}/${currentTenantCd}/view/${workbook}/${dashboard}`);

      store.dispatch({
        type: ReportTypes.OPEN_REPORT_VIEWER_REQUEST,
        reportUrl: card.reportSource ? card.reportSource : formatReptSource(tableauServiceUrl, currentTenantCd, card.reptSource),
        category: card.category,
        title: card.displayText,
        reportBaseUrl,
        report: card,
        reportOriginComponent
      });
      sessionStorage.setItem('reportOrigin', JSON.stringify({ reportBaseUrl, reportOriginComponent, reportTitle: card.displayText }));
    },
    [currentTenantCd, tableauServiceUrl, navigate, location.pathname, location.search, tabValue]
  );

  const refreshReports = useCallback(async () => {
    try {
      const response = await getReportsData();
      const sorted = sortByAccessDateDesc(response);
      reportsCache.data = sorted;
      reportsCache.ts = Date.now();
      startTransition(() => {
        setReportsData(sorted);
      });
      store.dispatch(FavoriteReportActions.getFavoriteReport());
    } catch (error) {
      console.error('Error refreshing reports data:', error);
    }
  }, []);

  useEffect(() => {
    if (!Array.isArray(reduxFavorites)) return;
    setReportsData((prev) => reconcileFavorites(prev, reduxFavorites));
    reportsCache.data = reconcileFavorites(reportsCache.data, reduxFavorites);
  }, [reduxFavorites]);
  
  const handleUnsaveFavorites = useCallback(
    async (items) => {
      if (!Array.isArray(items) || items.length === 0) return;
      try {
        await Promise.all(
          items.map((card) =>
            Promise.resolve(
              store.dispatch(
                FavoriteReportActions.deleteFavoriteReport(card.id, card.category, card.category, false)
              )
            )
          )
        );
      } finally {
        await refreshReports();
      }
    },
    [refreshReports]
  );

  const handleShareClick = (card) => {
    alert("shared clicked for ReportSummary");
  };

  const toggleFavorite = async (card) => {

    if (!card) return;
    try {
      // Optimistic UI update first so the icon changes immediately
      const flipFavorited = (list) => {
        if (!Array.isArray(list)) return list;
        return list.map((r) => (r.id === card.id ? { ...r, favorited: !r.favorited } : r));
      };

      setReportsData((prev) => {
        const next = flipFavorited(prev);
        reportsCache.data = Array.isArray(reportsCache.data) ? flipFavorited(reportsCache.data) : next;
        return next;
      });

      // Fire the server action without awaiting it; refresh will run after completion to reconcile
      const action = card.favorited
        ? FavoriteReportActions.deleteFavoriteReport(card.id, card.category, card.category, false)
        : FavoriteReportActions.addFavoriteReport(card.id, card.category, false);

      // Dispatch returns a promise (thunk returns axios promise). Handle resolution/failure to ensure eventual consistency.
      const dispatchPromise = store.dispatch(action);
      dispatchPromise
        .then(() => {
          // Instead of fetching all reports, fetch only favorite-report to avoid calling /report/all
          store.dispatch(FavoriteReportActions.getFavoriteReport());
        })
        .catch(async (err) => {
          console.error('Error toggling favorite (server):', err);
          // Reconcile by refreshing the data from server (full refresh)
          await refreshReports();
        });

      return;
    } catch (err) {
       // On error, refresh from server to reconcile
       console.error('Error toggling favorite (unexpected):', err);
       await refreshReports();
       return;
     }
  };

  const emptyStateText =
    tabValue === 'recently_viewed' ? 'No recently viewed reports.' : tabValue === 'saved' ? 'Your team has not saved any reports.' : 'No needs attention reports available.';

  const isDataLoading = isReportLoading || Boolean(topFavoritesLoading);

  const tabsWidth = 402;

  return (
    <>
      <Box
        ref={headerRef}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'flex-start' },
          justifyContent: 'space-between',
          gap: 2,
          width: '100%',
          marginRight: isReports ? undefined : 2,
          px: isReports ? {md:'32px', lg: '40px', xl:'48px'} : undefined,
          mx: isReports ? { md: 'auto' } : undefined,
        }}>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: "16px", flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: 'rubik',
              fontSize: 20,
              fontWeight: 500,
              fontStyle: 'normal',
              lineHeight: '30px',
              color: '#121A22'
            }}>
            Reports
          </Typography>
          {!location.pathname.includes('/bi/reports') && (
            <Typography variant="body2" sx={{ color: '#121A22', fontSize: '14px', fontFamily: 'Noto Sans', fontStyle: 'normal', fontWeight: 400, lineHeight: '21px', letterSpacing: 0 }}>
              Explore prebuilt reports with consistent KPIs and clear views of plan performance.
            </Typography>
          )}
        </Box>

        {!location.pathname.includes('/bi/reports') ? (
          <SeeAllReportsLink sx={{ mt: 0.5, display: { xs: 'none', sm: 'inline-flex' } }} />
        ) : (
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2,
            mt: { xs: 2, sm: -0.25 },
            mx: { xs: buttonRowBleed ? '-8px' : 0, sm: 0 },
            width: { xs: buttonRowBleed ? 'calc(100% + 16px)' : '100%', sm: 'auto' },
          }}>
            {hasPermission(AppConfig.PERMISSION.FO_AD_HOC, permissions) && (
              <ReportActionButton
                startIcon={<IconExternalLink size={16} />}
                onClick={() => {
                  trackClick(trackingTypes.APPLICATION, trackingActions.SWITCH, AppConfig.PERMISSION.FO_AD_HOC);
                  onClickTableauAdhoc();
                }}>
                Create Ad Hoc Report
              </ReportActionButton>
            )}
            <ReportActionButton
              startIcon={<IconAdjustmentsHorizontal size={16} />}
              onClick={() => setIsManageSavedOpen(true)}>
              Manage Saved Reports
            </ReportActionButton>
          </Box>
        )}
      </Box>

      {/* Grey band: bled to fill the main area and aligned to the header by the
          useLayoutEffect above. Inline margin/width/padding are owned by that effect. */}
      <Box
        ref={greyRef}
        sx={{
          position: 'relative',
          width: '100%',
          mt: '24px',
          py: isReports ? '24px' : 0,
          mb: isReports ? '24px' : 0,
          boxSizing: 'border-box',
          backgroundColor: isReports ? '#F8F8F8' : 'transparent',
        }}>
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          px: isReports ? {md:'32px', lg: '40px', xl:'48px'} : undefined,
        }}>
        {/* Tabs and Arrows */}
        <Box
          ref={controlsRef}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            mb: '24px'
          }}>
          <SegmentedTabs value={tabValue} onChange={handleTabsChange} height={44} width={tabsWidth} fullWidth={false} marginLeft={0} />
          {showArrows && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                aria-label="Previous"
                onClick={handlePrevious}
                disabled={isAtStart}
                disableRipple
                disableFocusRipple
                disableTouchRipple
                sx={{
                  width: 44,
                  height: 44,
                  border: '1px solid',
                  borderColor: isAtStart ? '#B0B0B0' : '#2F54A5',
                  borderRadius: '50%',
                  bgcolor: '#FFFFFF',
                  color: isAtStart ? '#B0B0B0' : '#2F54A5',
                  '&:hover': {
                    backgroundColor: '#D8E6FF',
                    borderColor: '#001A47',
                    color: '#001A47',
                    '& svg': { color: '#001A47' }
                  },
                  '&:focus': { outline: 'none' },
                  '&:active': { backgroundColor: '#FFFFFF' },
                  '&.Mui-disabled': { bgcolor: '#FFFFFF' }
                }}>
                <IconChevronLeft size={20} color='currentColor' />
              </IconButton>
              <IconButton
                aria-label="Next"
                onClick={handleNext}
                disabled={isAtEnd}
                disableRipple
                disableFocusRipple
                disableTouchRipple
                sx={{
                  width: 44,
                  height: 44,
                  border: '1px solid',
                  borderColor: isAtEnd ? '#B0B0B0' : '#2F54A5',
                  borderRadius: '50%',
                  bgcolor: '#FFFFFF',
                  color: isAtEnd ? '#B0B0B0' : '#2F54A5',
                  '&:hover': {
                    backgroundColor: '#D8E6FF',
                    borderColor: '#001A47',
                    color: '#001A47',
                    '& svg': { color: '#001A47' }
                  },
                  '&:focus': { outline: 'none' },
                  '&:active': { backgroundColor: '#FFFFFF' },
                  '&.Mui-disabled': { bgcolor: '#FFFFFF' }
                }}>
                <IconChevronRight size={20} color='currentColor' />
              </IconButton>
            </Box>
          )}
        </Box>

        {/* Empty state block */}
        {isDataLoading ? (
          <ReportSectionLoading count={4} variant="grid" />
        ) : cardsForTab.length === 0 ? (
          <Box
            role="status"
            aria-live="polite"
            sx={{
              width: '100%',
              minHeight: `${layout.cardHeight}px`,
              borderRadius: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 4,
              bgcolor: '#F8F8F8',
              border: '1px solid #F8F8F8',
              textAlign: 'center',
              mb: { xs: 3, sm: 0 }
            }}>
            <Typography
              sx={{
                color: 'var(--ctev-black, #000)',
                leadingTrim: 'both',
                textEdge: 'cap',
                fontFamily: 'Noto Sans',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: '150%'
              }}>
              {emptyStateText}
            </Typography>
          </Box>
        ) : (
          <Box
            ref={scrollContainerRef}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: `${layout.gapValue}px`,
              overflowX: 'auto',
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
                mx: { xs: -2, sm: -6 },
                px: { xs: 2, sm: 6 },
                scrollPaddingLeft: { xs: '16px', sm: '48px' },
                scrollPaddingRight: { xs: '16px', sm: '48px' },
            }}>
            {cardsForTab.map((card, idx) => (
              <ReportSummaryItem
                key={card.id ?? card.reptSource ?? card.reportSource ?? `carousel-${tabValue}-${idx}`}
                card={card}
                idx={idx}
                start={0}
                layout={layout}
                tabValue={tabValue}
                favoriteReportsCount={card?.favoriteCount || 0}
                handleReportClick={handleReportClick}
                handleShareClick={handleShareClick}
                isReports={isReports}
                carouselConfig={carouselConfig}
              />
            ))}
          </Box>
        )}
      </Box>
      </Box>
      {/* Mobile layout — centered below cards */}
      {!location.pathname.includes('/bi/reports') && (
        <Box sx={{ display: { xs: 'flex', sm: 'none' }, justifyContent: 'center', pb: 5 }}>
          <SeeAllReportsLink />
        </Box>
      )}

      <ManageSavedReportsDrawer
        open={isManageSavedOpen}
        onClose={() => setIsManageSavedOpen(false)}
        reports={favoriteReports}
        onUnsaveReports={handleUnsaveFavorites}
      />
      {location.pathname.includes('/bi/reports') && hasPermission(AppConfig.PERMISSION.FO_AD_HOC, permissions) && (
        <AlertDialog handleComfirm={() => store.dispatch(ReportActions.navigateToAdhoc())} />
      )}
    </>
  );
});

export default ReportSummary;
